import React, { useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Send } from "lucide-react";

const initial = {
  name: "",
  company: "",
  email: "",
  phone: "",
  description: "",
};

export const ServiceRequestForm = () => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form, company: form.company || undefined };
      const { data } = await api.post("/service-requests", payload);
      toast.success("Request received — we'll be in touch shortly.");
      setSuccess(data);
      setForm(initial);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="service-request-form-container"
      className="bg-white border border-slate-200 p-8 md:p-10 shadow-[0_8px_32px_-12px_rgba(15,23,42,0.12)]"
    >
      <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-blue-600">
        Book Service
      </div>
      <h3 className="font-heading mt-2 text-3xl md:text-4xl font-black tracking-tighter uppercase text-slate-900">
        Request a Technician
      </h3>
      <p className="mt-3 text-slate-600">
        Tell us about your unit and we'll route the right tech to your site.
      </p>

      {success ? (
        <div
          data-testid="service-request-success"
          className="mt-8 border border-green-300 bg-green-50 p-6"
        >
          <div className="flex items-start gap-3">
            <ShieldCheck className="h-5 w-5 text-green-700 mt-0.5" />
            <div>
              <div className="font-heading font-bold text-green-900">
                Request #{success.id.slice(0, 8).toUpperCase()} received.
              </div>
              <p className="text-sm text-green-800 mt-1">
                A Pneupro coordinator will contact you at {success.email} shortly.
              </p>
              <button
                onClick={() => setSuccess(null)}
                data-testid="service-request-new-button"
                className="mt-4 text-xs font-bold tracking-widest uppercase text-green-900 underline underline-offset-4"
              >
                Submit another request
              </button>
            </div>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wide text-slate-900" htmlFor="sr-name">
              Full Name *
            </Label>
            <Input
              id="sr-name"
              data-testid="sr-input-name"
              required
              minLength={2}
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              className="mt-2 rounded-none"
            />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wide text-slate-900" htmlFor="sr-company">
              Company
            </Label>
            <Input
              id="sr-company"
              data-testid="sr-input-company"
              value={form.company}
              onChange={(e) => update("company", e.target.value)}
              className="mt-2 rounded-none"
            />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wide text-slate-900" htmlFor="sr-email">
              Email *
            </Label>
            <Input
              id="sr-email"
              data-testid="sr-input-email"
              type="email"
              required
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              className="mt-2 rounded-none"
            />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wide text-slate-900" htmlFor="sr-phone">
              Phone *
            </Label>
            <Input
              id="sr-phone"
              data-testid="sr-input-phone"
              required
              value={form.phone}
              onChange={(e) => update("phone", e.target.value)}
              className="mt-2 rounded-none"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs font-bold uppercase tracking-wide text-slate-900" htmlFor="sr-desc">
              Describe the issue *
            </Label>
            <Textarea
              id="sr-desc"
              data-testid="sr-input-description"
              required
              minLength={5}
              rows={6}
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              className="mt-2 rounded-none"
              placeholder="Compressor make/model, symptoms, error codes, runtime hours, preferred timing..."
            />
          </div>
          <div className="md:col-span-2 pt-2">
            <button
              type="submit"
              data-testid="sr-submit-button"
              disabled={submitting}
              className="w-full md:w-auto bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 font-bold tracking-wide uppercase px-8 py-4 inline-flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Submitting…
                </>
              ) : (
                <>
                  Submit Request <Send className="h-4 w-4" />
                </>
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default ServiceRequestForm;
